import { delay } from 'redux-saga';
import { apply, put, select, takeEvery, takeLatest } from 'redux-saga/effects';

import { State as RootState } from '@occc-app/rootRedux/types';
import { getSingleton } from '@occc-app/Activate/api';
import { CSVUpload as CSVUploadAPI } from '@occc-app/Activate/apiTypes';
import { ClientError } from '@occc-app/lib/client';
import { safeAsync } from '@occc-app/lib/safeAsync';

import types, { ActionCreators, State } from './types';
import actions from './actions';
import { deserializeCSVUpload } from './deserializers';
import { serializeCSVUploadCreate } from './serializers';

const POLL_CSV_UPLOADS_EVERY_MS = 5000;

type CSVUploadsState = { csvUploads: State['csvUploads'] };
type GetCSVUploadsFromState = (state: RootState) => CSVUploadsState;
const getCSVUploadsFromState: GetCSVUploadsFromState = (state) => ({
  csvUploads: state.csvUpload.csvUploads,
});

/* Create CSV Upload Saga */
function* createCSVUpload(action: ReturnType<ActionCreators['CreateCSVUpload']>) {
  const chainId = action.chainId;

  const core = getSingleton();
  const serializerConfig = {
    action: action.action,
    chainId,
    config: action.config,
    file: action.file,
  };

  const body = serializeCSVUploadCreate(serializerConfig);
  const csvUploadData = (yield apply(core, core.csvUploads.create, [body, {}])) as {
    data: CSVUploadAPI;
  };

  const csvUpload = deserializeCSVUpload(csvUploadData.data);

  yield put(actions.createCSVUploadOK(csvUpload));
  yield put(actions.polledRequestCSVUpload(csvUpload.id, action.config.refetchSegments, chainId));
}

/* Request CSV Upload Saga */
function* requestCSVUploads(action: ReturnType<ActionCreators['RequestCSVUploads']>) {
  const core = getSingleton();
  try {
    const chainId = action.chainId;
    const params: any = {
      // it seems like we missing the closing bracket here: "filter[chain_id]"
      'filter[chain_id': chainId,
    };
    if (action.csvUploadIds.length > 0) {
      params['filter[id]'] = action.csvUploadIds.join(',');
    }
    if (action.action) {
      params['filter[action]'] = action.action;
    }

    const csvUploadsData = (yield apply(core, core.csvUploads.index, [params])) as {
      data: CSVUploadAPI[];
    };

    const csvUploads = csvUploadsData.data.map(deserializeCSVUpload);
    yield put(actions.requestCSVUploadsOK(csvUploads));
  } catch (error) {
    const errorMessages = error.errorMessages || [
      { message: `Unable to request CSV Uploads: ${action.csvUploadIds}` },
    ];
    yield put(actions.requestCSVUploadsERR(errorMessages, error));
  }
}

/* Request CSV Upload Saga */
function* requestCSVUpload(action: ReturnType<ActionCreators['RequestCSVUpload']>) {
  const core = getSingleton();
  const csvUploadData = (yield apply(core, core.csvUploads.id(action.csvUploadId).show, [{}])) as {
    data: CSVUploadAPI;
  };

  const csvUpload = deserializeCSVUpload(csvUploadData.data);

  yield put(actions.requestCSVUploadOK(csvUpload));
}

/* Polled Request CSV Upload Saga */
function* polledRequestCSVUploads(action: ReturnType<ActionCreators['PolledCSVUploads']>) {
  const csvUploadIds = action.csvUploadIds || [];
  const polled = action.polled;
  const state = (yield select(getCSVUploadsFromState)) as CSVUploadsState;

  const csvUploadsIncomplete = state.csvUploads
    .filter((csvUpload) => csvUploadIds.includes(csvUpload.id))
    .filter((csvUpload) => !['completed', 'failed'].includes(csvUpload.state));

  const poll = (csvUploadsIncomplete.length === 0 && !polled) || csvUploadsIncomplete.length > 0;
  if (poll) {
    const csvUploadAction = action.action;
    const actionConfig = {
      type: types.REQUEST_CSV_UPLOADS,
      action: csvUploadAction || '',
      csvUploadIds: csvUploadIds.length > 0 ? csvUploadIds : [],
      chainId: action.chainId,
    };

    yield requestCSVUploads(actionConfig);

    const newState = (yield select(getCSVUploadsFromState)) as CSVUploadsState;
    const requestedCsvUploadIds = newState.csvUploads.map((csvUpload) => csvUpload.id);

    yield delay(POLL_CSV_UPLOADS_EVERY_MS);

    yield put(
      actions.polledRequestCSVUploads({
        csvUploadIds: requestedCsvUploadIds,
        polled: true,
        chainId: action.chainId,
      }),
    );
  }
}

/* Polled Request CSV Upload Saga */
function* polledRequestCSVUpload(action: ReturnType<ActionCreators['PolledCSVUpload']>) {
  const csvUploadId = action.csvUploadId;
  const refetchSegments = action.refetchSegments;
  const state = (yield select(getCSVUploadsFromState)) as CSVUploadsState;

  const csvUploadIncomplete = state.csvUploads
    .filter((csvUpload) => csvUpload.id === csvUploadId)
    .filter((csvUpload) => !['completed', 'failed'].includes(csvUpload.state))[0];

  if (csvUploadIncomplete) {
    const actionConfig = {
      type: types.REQUEST_CSV_UPLOAD,
      csvUploadId,
      chainId: action.chainId,
    };
    yield requestCSVUpload(actionConfig);

    yield delay(POLL_CSV_UPLOADS_EVERY_MS);

    yield put(actions.polledRequestCSVUpload(csvUploadId, refetchSegments, action.chainId));
  }

  if (refetchSegments) {
    yield refetchSegments();
  }
}

const sagas = {
  createCSVUpload: safeAsync(createCSVUpload, (error: ClientError) => {
    const errorMessages = error.errorMessages || [{ message: 'Unable to create CSV Upload.' }];
    return actions.createCSVUploadERR(errorMessages, error);
  }),
  // TODO: figure out what to do when the poller fails
  polledRequestCSVUpload: safeAsync(polledRequestCSVUpload),
  // TODO: figure out what to do when the poller fails
  polledRequestCSVUploads: safeAsync(polledRequestCSVUploads),
  requestCSVUpload: safeAsync(requestCSVUpload, (error: ClientError, action) => {
    const errorMessages = error.errorMessages || [
      { message: `Unable to request CSV Upload: ${action.csvUploadId}` },
    ];
    return actions.requestCSVUploadERR(errorMessages, error);
  }),
  requestCSVUploads: safeAsync(requestCSVUploads, (error: ClientError, action) => {
    const errorMessages = error.errorMessages || [
      { message: `Unable to request CSV Uploads: ${action.csvUploadIds}` },
    ];
    return actions.requestCSVUploadsERR(errorMessages, error);
  }),
};

export default function* saga() {
  yield takeLatest(types.CREATE_CSV_UPLOAD, sagas.createCSVUpload);
  yield takeEvery(types.POLLED_REQUEST_CSV_UPLOAD, sagas.polledRequestCSVUpload);
  yield takeLatest(types.POLLED_REQUEST_CSV_UPLOADS, sagas.polledRequestCSVUploads);
  yield takeLatest(types.REQUEST_CSV_UPLOAD, sagas.requestCSVUpload);
  yield takeLatest(types.REQUEST_CSV_UPLOADS, sagas.requestCSVUploads);
}
